import { FORM_KEYS } from './account-properties.schema';
import { buildPropsFromFormState } from './account-properties.utils';
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function deepEqual(a, b) {
    if (a === b)
        return true;
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
    }
    if (isPlainObject(a) && isPlainObject(b)) {
        const aKeys = Object.keys(a);
        return (aKeys.length === Object.keys(b).length &&
            aKeys.every((k) => k in b && deepEqual(a[k], b[k])));
    }
    return false;
}
// Granular diff: the returned patch contains only the sub-paths where `next`
// differs from `prev`. Plain objects recurse; arrays and primitives are atomic.
// A key present in `prev` but missing in `next` yields `undefined` (a deletion).
function diffValue(prev, next) {
    if (isPlainObject(prev) && isPlainObject(next)) {
        const patch = {};
        let changed = false;
        // eslint-disable-next-line unicorn/prefer-iterator-concat
        for (const key of new Set([...Object.keys(prev), ...Object.keys(next)])) {
            const res = diffValue(prev[key], next[key]);
            if (res.changed) {
                patch[key] = res.patch;
                changed = true;
            }
        }
        return { changed, patch };
    }
    if (deepEqual(prev, next))
        return { changed: false, patch: undefined };
    return { changed: true, patch: next };
}
// Applies a patch from diffValue onto `target` in place: plain objects merge
// recursively, arrays/primitives replace, and `undefined` deletes the key.
function applyPatch(target, patch) {
    for (const key of Object.keys(patch)) {
        const value = patch[key];
        const existing = target[key];
        if (value === undefined) {
            Reflect.deleteProperty(target, key);
        }
        else if (isPlainObject(value) && isPlainObject(existing)) {
            applyPatch(existing, value);
        }
        else {
            target[key] = value;
        }
    }
}
export function buildSparsePayload(originalProps, baselineState, currentState) {
    const builtBaseline = buildPropsFromFormState(baselineState);
    const builtCurrent = buildPropsFromFormState(currentState);
    // Diff only the form-modeled keys. Unknown / advanced top-level keys are
    // reconciled separately so deletions in the Advanced JSON field take effect.
    const patch = {};
    for (const key of FORM_KEYS) {
        const res = diffValue(builtBaseline[key], builtCurrent[key]);
        if (res.changed)
            patch[key] = res.patch;
    }
    const merged = structuredClone(originalProps ?? {});
    applyPatch(merged, patch);
    // The Advanced JSON textarea is authoritative for unknown top-level keys:
    // keep only the form-modeled slice, then re-apply the textarea so add / edit
    // / delete of unknown keys all round-trip.
    const payload = Object.fromEntries(Object.entries(merged).filter(([key]) => FORM_KEYS.has(key)));
    if (currentState.advancedJson.trim()) {
        Object.assign(payload, JSON.parse(currentState.advancedJson));
    }
    return payload;
}
